import { useCallback, useEffect, useRef } from 'react';

import { type PracticeDrill, startPracticeSession, updatePracticeSession } from '@/api';

export function useDrillCursor(
  drill: PracticeDrill | null,
  resumedId: string | null,
  resumedFrom: number,
) {
  const id = useRef<string | null>(null);
  const from = useRef(0);
  const furthest = useRef(0);
  const finished = useRef(false);

  useEffect(() => {
    finished.current = false;
    if (resumedId !== null) {
      id.current = resumedId;
      from.current = resumedFrom;
      furthest.current = resumedFrom;
      return;
    }
    if (drill === null) return;
    id.current = null;
    from.current = 0;
    furthest.current = 0;
    let cancelled = false;
    startPracticeSession(drill)
      .then((started) => {
        if (cancelled) return;
        if (finished.current) {
          void updatePracticeSession(started.id, { status: 'finished' }).catch(() => {});
          return;
        }
        id.current = started.id;
        if (furthest.current > 0)
          void updatePracticeSession(started.id, { position: furthest.current }).catch(() => {});
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [drill, resumedId, resumedFrom]);

  const advance = useCallback((index: number) => {
    const position = from.current + index;
    if (position <= furthest.current) return;
    furthest.current = position;
    if (id.current) {
      void updatePracticeSession(id.current, { position }).catch(() => {});
    }
  }, []);

  const finish = useCallback(() => {
    finished.current = true;
    if (id.current) void updatePracticeSession(id.current, { status: 'finished' }).catch(() => {});
    id.current = null;
  }, []);

  return { advance, finish };
}
